import request from '@/Network/Requests/request'
import Service from '@/Network/Services/Service'
import type {
  AllocationInvitePreviewRequest,
  CreateAllocationInviteRequest,
  PendingCommitmentsRequest
} from '@/Network/Types/Requests/AllocationInviteRequests'
import type {
  AllocationInviteDetail,
  AllocationInvitePreview,
  CreatedAllocationInvite,
  PendingCommitment
} from '@/Network/Types/Responses/AllocationInviteResponses'

/**
 * Convite de alocação do escalista (acceptance-terms, spec §7). Todas as rotas são
 * `company-jwt` e respondem 404 com `ALLOCATION_INVITE_ENABLED` desligada no backend.
 */
export default class AllocationInviteService extends Service {
  constructor() {
    super()
    this.prefix = '/allocation-invites'
  }

  /** Prévia do passo 3: elegíveis, já iniciados, avisos por médico e vencimento. */
  async preview(body: AllocationInvitePreviewRequest): Promise<AllocationInvitePreview> {
    try {
      const { data } = await request.post<AllocationInvitePreview>(
        this.getCompletePath('/preview'),
        body
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível montar a prévia do convite')
    }
  }

  /** Cria o convite e devolve o link de cada convidado (única vez, D32). */
  async create(body: CreateAllocationInviteRequest): Promise<CreatedAllocationInvite> {
    try {
      const { data } = await request.post<CreatedAllocationInvite>(this.getCompletePath(), body)
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível enviar o convite')
    }
  }

  /** Trocas e convites pendentes dos plantões (aviso antes de alocar direto, D15/D54). */
  async pendingCommitments(stationIds: number[]): Promise<PendingCommitment[]> {
    const body: PendingCommitmentsRequest = { stationIds }
    try {
      const { data } = await request.post<PendingCommitment[]>(
        this.getCompletePath('/pending-commitments'),
        body
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível verificar as pendências dos plantões')
    }
  }

  /** Detalhe do convite: convidados, plantões e histórico (`station_basic_reading`). */
  async getDetail(inviteId: number): Promise<AllocationInviteDetail> {
    try {
      const { data } = await request.get<AllocationInviteDetail>(
        this.getCompletePath(`/${inviteId}`)
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível carregar o convite')
    }
  }

  /** Cancela o convite (D38): os plantões voltam a ser vaga aberta comum. */
  async cancel(inviteId: number): Promise<AllocationInviteDetail> {
    try {
      const { data } = await request.post<AllocationInviteDetail>(
        this.getCompletePath(`/${inviteId}/cancel`),
        {}
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível cancelar o convite')
    }
  }

  /** Reenvia aos convidados pendentes; cada um recebe um link novo (D56). */
  async resend(inviteId: number): Promise<AllocationInviteDetail> {
    try {
      const { data } = await request.post<AllocationInviteDetail>(
        this.getCompletePath(`/${inviteId}/resend`),
        {}
      )
      return data
    } catch (e) {
      throw this.handleError(e, 'Não foi possível reenviar o convite')
    }
  }

  /** Gera um link novo para o convidado; o anterior deixa de funcionar (D56). A geração é auditada. */
  async regenerateLink(inviteId: number, inviteeId: number): Promise<string> {
    try {
      const { data } = await request.post<{ url: string }>(
        this.getCompletePath(`/${inviteId}/invitees/${inviteeId}/link`),
        {}
      )
      return data.url
    } catch (e) {
      throw this.handleError(e, 'Não foi possível gerar o link')
    }
  }

  /** Remove um convidado; remover o último pendente cancela o convite. */
  async removeInvitee(inviteId: number, inviteeId: number): Promise<AllocationInviteDetail> {
    try {
      const { data } = await request.delete(
        this.getCompletePath(`/${inviteId}/invitees/${inviteeId}`)
      )
      return data as AllocationInviteDetail
    } catch (e) {
      throw this.handleError(e, 'Não foi possível remover o convidado')
    }
  }
}
